import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CHECKLIST_ITEMS, DEAL_PHASES, LEAD_PRIORITIES, PRIORITY_BADGE, PROPERTY_TYPES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/PlaceholderPage";

const ALL = "__all__";

const checklistDone = (d) => CHECKLIST_ITEMS.filter((c) => d[c.field]).length;

const FilterSelect = ({ value, onChange, placeholder, options, testId, className }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`rounded-lg bg-white ${className || "w-[160px]"}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return <SelectItem key={v} value={v}>{l}</SelectItem>;
      })}
    </SelectContent>
  </Select>
);

function DealCard({ deal, onDragStart }) {
  const navigate = useNavigate();
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      onClick={() => navigate(`/deals/${deal.id}`)}
      data-testid={`deal-card-${deal.id}`}
      className="bg-white rounded-lg border border-slate-200 p-3.5 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md hover:border-emerald-300 transition-all duration-200 space-y-2"
    >
      <p className="font-medium text-emerald-950 text-sm leading-snug">{deal.customer_name}</p>
      <p className="text-xs text-slate-500 leading-snug">{deal.property_title}</p>
      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-xs text-slate-400 truncate">{deal.assigned_to_name || "Unassigned"}</span>
        <div className="flex items-center gap-1.5">
          {deal.phase === "Discussion" && (
            <span className="text-[11px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid={`deal-checklist-count-${deal.id}`}>
              {checklistDone(deal)}/5 tasks
            </span>
          )}
          {deal.lead_priority && (
            <Badge variant="outline" className={`${PRIORITY_BADGE[deal.lead_priority]} text-[10px] px-1.5 py-0`}>{deal.lead_priority}</Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function Board({ isAdmin }) {
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: isAdmin,
  });
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ assigned: ALL, type: ALL, priority: ALL });
  const [pendingMove, setPendingMove] = useState(null);
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const params = { outcome: "Active" };
  if (isAdmin && filters.assigned !== ALL) params.assigned_to = filters.assigned;
  if (filters.type !== ALL) params.property_type = filters.type;
  if (filters.priority !== ALL) params.priority = filters.priority;

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["deals", params],
    queryFn: async () => (await api.get("/deals", { params })).data,
  });

  const phaseMutation = useMutation({
    mutationFn: async ({ dealId, phase }) => (await api.patch(`/deals/${dealId}/phase`, { phase })).data,
    onSuccess: (_, { phase }) => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      toast.success(`Deal moved to ${phase}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const onDragStart = (e, deal) => e.dataTransfer.setData("dealId", deal.id);

  const onDrop = (e, phase) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData("dealId");
    const deal = deals.find((d) => d.id === dealId);
    if (!deal || deal.phase === phase) return;
    if (deal.phase === "Discussion" && checklistDone(deal) < 5) {
      setPendingMove({ deal, phase });
    } else {
      phaseMutation.mutate({ dealId, phase });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {isAdmin && (
          <FilterSelect
            value={filters.assigned}
            onChange={set("assigned")}
            placeholder="All Staff"
            options={users.map((u) => ({ value: u.id, label: u.full_name }))}
            testId="deal-filter-assigned"
            className="w-[170px]"
          />
        )}
        <FilterSelect value={filters.type} onChange={set("type")} placeholder="All Types" options={PROPERTY_TYPES} testId="deal-filter-type" />
        <FilterSelect value={filters.priority} onChange={set("priority")} placeholder="All Priorities" options={LEAD_PRIORITIES} testId="deal-filter-priority" className="w-[150px]" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {DEAL_PHASES.map((phase) => {
          const column = deals.filter((d) => d.phase === phase);
          return (
            <div
              key={phase}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, phase)}
              data-testid={`board-column-${phase.replace(/\s/g, "-")}`}
              className="bg-slate-50/80 rounded-xl border border-slate-200/70 p-3 min-h-[300px]"
            >
              <div className="flex items-center justify-between px-1 mb-3">
                <h3 className="font-heading text-sm font-semibold text-emerald-950">{phase}</h3>
                <span className="text-xs text-slate-400 bg-white border border-slate-200 rounded-full px-2 py-0.5">{column.length}</span>
              </div>
              <div className="space-y-2.5">
                {isLoading ? (
                  <p className="text-xs text-slate-400 px-1">Loading…</p>
                ) : (
                  column.map((d) => <DealCard key={d.id} deal={d} onDragStart={onDragStart} />)
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!pendingMove} onOpenChange={(open) => !open && setPendingMove(null)}>
        <AlertDialogContent className="rounded-xl" data-testid="checklist-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discussion tasks incomplete</AlertDialogTitle>
            <AlertDialogDescription>
              Some discussion tasks aren't marked complete yet ({pendingMove ? checklistDone(pendingMove.deal) : 0}/5 done) — move to {pendingMove?.phase} anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="checklist-confirm-move"
              className="rounded-lg bg-emerald-900 hover:bg-emerald-950"
              onClick={() => {
                phaseMutation.mutate({ dealId: pendingMove.deal.id, phase: pendingMove.phase });
                setPendingMove(null);
              }}
            >
              Move Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ClosedDeals({ initialOutcome }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    outcome: initialOutcome && ["Done", "Dropped"].includes(initialOutcome) ? initialOutcome : ALL,
    type: ALL, from: "", to: "",
  });
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const params = { outcome: filters.outcome === ALL ? "closed" : filters.outcome };
  if (filters.type !== ALL) params.property_type = filters.type;
  if (filters.from) params.closed_from = filters.from;
  if (filters.to) params.closed_to = filters.to;

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["deals-closed", params],
    queryFn: async () => (await api.get("/deals", { params })).data,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <FilterSelect value={filters.outcome} onChange={set("outcome")} placeholder="Done & Dropped" options={["Done", "Dropped"]} testId="closed-filter-outcome" />
        <FilterSelect value={filters.type} onChange={set("type")} placeholder="All Types" options={PROPERTY_TYPES} testId="closed-filter-type" />
        <Input type="date" value={filters.from} onChange={(e) => set("from")(e.target.value)} data-testid="closed-filter-from" className="w-[160px] rounded-lg bg-white" />
        <span className="text-slate-400 text-sm">to</span>
        <Input type="date" value={filters.to} onChange={(e) => set("to")(e.target.value)} data-testid="closed-filter-to" className="w-[160px] rounded-lg bg-white" />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/60">
              <TableHead>Customer</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Closed On</TableHead>
              <TableHead>Dropped Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
            ) : deals.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400" data-testid="closed-deals-empty">No closed deals match these filters.</TableCell></TableRow>
            ) : (
              deals.map((d) => (
                <TableRow key={d.id} onClick={() => navigate(`/deals/${d.id}`)} data-testid={`closed-deal-row-${d.id}`} className="cursor-pointer hover:bg-emerald-50/40">
                  <TableCell className="font-medium text-emerald-950">{d.customer_name}</TableCell>
                  <TableCell className="text-slate-600 max-w-[220px] truncate">{d.property_title}</TableCell>
                  <TableCell className="text-slate-600">{d.property_type}</TableCell>
                  <TableCell className="text-slate-600">{d.assigned_to_name || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={d.outcome === "Done" ? "border-emerald-400 text-emerald-900 bg-emerald-100" : "border-red-200 text-red-600 bg-red-50"}>
                      {d.outcome}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-600">{d.closed_at ? new Date(d.closed_at).toLocaleDateString("en-IN") : "—"}</TableCell>
                  <TableCell className="text-slate-500 max-w-[200px] truncate">{d.dropped_reason || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function DealsPage() {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [searchParams] = useSearchParams();

  return (
    <div className="p-6 sm:p-8" data-testid="deals-page">
      <PageHeader title="Deals Pipeline" subtitle={isAdmin ? "All deals — drag cards between phases" : "Your assigned deals — drag cards between phases"} />
      <Tabs defaultValue={searchParams.get("tab") === "closed" ? "closed" : "board"}>
        <TabsList className="rounded-lg mb-6">
          <TabsTrigger value="board" data-testid="tab-board" className="rounded-md">Board</TabsTrigger>
          <TabsTrigger value="closed" data-testid="tab-closed" className="rounded-md">Closed Deals</TabsTrigger>
        </TabsList>
        <TabsContent value="board" className="animate-fade-up">
          <Board isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="closed" className="animate-fade-up">
          <ClosedDeals initialOutcome={searchParams.get("outcome")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
